import { Slot } from 'radix-ui';
import { createContext, useContext, type ComponentPropsWithoutRef, type FC } from 'react';
import { portaledSurfaceVariants } from '../lib/portaledSurface';
import { cn } from '../lib/utils';

const DarkThemeScopeContext = createContext(false);

/**
 * Reports whether the caller sits inside a `DarkThemeScope`.
 *
 * A component that portals to `document.body` leaves the scope's DOM subtree, so it
 * cannot inherit the scope's `.dark` by ancestry. A component that serves both an
 * always-dark surface and the ordinary themed routes also cannot hardcode one variant.
 * Both cases read this to pick the `variant` for the overlay they open.
 *
 * Prefer a fixed `variant="dark"` when the caller only ever renders inside a scope.
 */
export const useIsDarkThemeScope = (): boolean => useContext(DarkThemeScopeContext);

export type DarkThemeScopeProps = ComponentPropsWithoutRef<'div'> & {
  /** Makes an existing element the scope itself (e.g. a page layout root) instead of adding a wrapper div. */
  asChild?: boolean;
};

/**
 * The dark surface every portaled overlay's `variant="dark"` applies to its
 * content. Derived from `portaledSurfaceVariants` so this set stays identical
 * across every consumer, including Popover, Select, Combobox, and DropdownMenu.
 */
export const DARK_OVERLAY_SURFACE_CLASSES = portaledSurfaceVariants({ variant: 'dark' });

/**
 * Forces dark chrome on a subtree regardless of the app-wide theme. Any surface
 * can wrap with this (video, floorplan, and others). Carries its own surface
 * classes rather than relying on `dark:`-prefixed ones, because
 * `@custom-variant dark` is `&:is(.dark *)` and only matches descendants — the same
 * reason `DialogContent`'s `variant="dark"` does the same thing. `tw` re-enables
 * Tailwind preflight for whatever subtree this scopes, matching how portaled content
 * (which mounts outside the app's own `tw`-scoped root) already has to.
 *
 * The default surface is the generic dark tokens. A consumer that needs a different
 * always-dark palette (the video player uses `--background-video`) passes it on
 * `className`.
 *
 * Portaled overlays escape this node. Pass `variant="dark"` on those overlays so
 * they match this surface.
 */
export const DarkThemeScope: FC<DarkThemeScopeProps> = ({
  asChild,
  className,
  children,
  ...props
}) => {
  const Comp = asChild ? Slot.Root : 'div';
  return (
    <DarkThemeScopeContext.Provider value={true}>
      <Comp
        data-slot="dark-theme-scope"
        className={cn('tw', DARK_OVERLAY_SURFACE_CLASSES, className)}
        {...props}
      >
        {children}
      </Comp>
    </DarkThemeScopeContext.Provider>
  );
};
